import React, { FC, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { faCancel, faSave, faWarning } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import styled from 'styled-components';
import Modal from 'components/Modal';
import AddressInput from 'components/AddressInput';
import useModalStack from 'contexts/modal/useModalStack';
import { CaseExtendedModel, CompanyModel, ZipCodeArea } from 'api/model';

const WarningContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px;
  margin-bottom: 20px;
  border: 1px solid ${({ theme }) => theme.colors.background.negative};
  color: ${({ theme }) => theme.colors.foreground.primary};
`;

const AddressSection = styled.div`
  margin-bottom: 24px;
`;

const SectionTitle = styled.h3`
  margin: 0 0 12px 0;
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.foreground.primary};
`;

interface OriginalAddressFormData {
  originalFromName: string;
  originalFromAddress: string;
  originalFromCity: string;
  originalFromZip: string;
  originalFromCounty: string;
  originalToName: string;
  originalToAddress: string;
  originalToCity: string;
  originalToZip: string;
  originalToCounty: string;
}

interface Props {
  caseData: CaseExtendedModel;
  onSave: (data: OriginalAddressFormData) => Promise<void>;
}

const OriginalAddressEditModal: FC<Props> = ({ caseData, onSave }) => {
  const modalStack = useModalStack();

  const defaultValues: OriginalAddressFormData = useMemo(() => {
    const countyToString = (county: string | ZipCodeArea | undefined) =>
      typeof county === 'string' ? county : (county?.id.toString() ?? '');

    const values = {
      originalFromName: caseData.case.originalFromName ?? '',
      originalFromAddress: caseData.case.originalFromAddress ?? '',
      originalFromCity: caseData.case.originalFromCity ?? '',
      originalFromZip: caseData.case.originalFromZip ?? '',
      originalFromCounty: countyToString(caseData.case.originalFromCounty),
      originalToName: caseData.case.originalToName ?? '',
      originalToAddress: caseData.case.originalToAddress ?? '',
      originalToCity: caseData.case.originalToCity ?? '',
      originalToZip: caseData.case.originalToZip ?? '',
      originalToCounty: countyToString(caseData.case.originalToCounty),
    };

    // Intentionally no console logging to satisfy linting rules

    return values;
  }, [caseData.case]);

  const {
    watch,
    setValue,
    getValues,
    reset,
    formState: { isDirty },
  } = useForm<OriginalAddressFormData>({
    defaultValues,
    mode: 'onChange',
  });

  const formData = watch();

  // Reset form when caseData changes
  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const handleCompanyPicked = (
    company: CompanyModel,
    newCounty: ZipCodeArea | undefined,
    fieldPrefix: 'originalFrom' | 'originalTo'
  ) => {
    // Company picked; populate fields from the provided company object
    setValue(
      `${fieldPrefix}Name` as keyof OriginalAddressFormData,
      company.name,
      { shouldDirty: true }
    );
    setValue(
      `${fieldPrefix}Address` as keyof OriginalAddressFormData,
      company.address,
      { shouldDirty: true }
    );
    setValue(
      `${fieldPrefix}City` as keyof OriginalAddressFormData,
      company.city,
      { shouldDirty: true }
    );
    setValue(
      `${fieldPrefix}Zip` as keyof OriginalAddressFormData,
      company.zip,
      { shouldDirty: true }
    );
    setValue(
      `${fieldPrefix}County` as keyof OriginalAddressFormData,
      newCounty?.id.toString() ?? '',
      { shouldDirty: true }
    );
  };

  // Build top options from case assignments so from/to addresses are quick picks
  const buildTopOptions = (which: 'from' | 'to') => {
    const opts: {
      label: string;
      value: number | null;
      extra?: CompanyModel | null;
    }[] = [];

    const assignments = caseData.case.assignmentList ?? [];
    for (const a of assignments) {
      const prefix = which === 'from' ? 'from' : 'to';
      const name = (a as any)[`${prefix}Name`] as string | undefined;
      const address = (a as any)[`${prefix}Address`] as string | undefined;
      const zip = (a as any)[`${prefix}Zip`] as string | undefined;
      const city = (a as any)[`${prefix}City`] as string | undefined;

      // Only add if there's some meaningful data
      if (name || address || zip) {
        const labelParts = [] as string[];
        if (name) labelParts.push(name);
        if (address) labelParts.push(address);
        if (zip) labelParts.push(zip);
        if (city) labelParts.push(city);

        const label = labelParts.join(', ');

        // Use negative id derived from assignmentID to avoid colliding with real companyIDs
        const value = a.assignmentID ? -Math.abs(a.assignmentID) : null;

        const syntheticCompany: CompanyModel = {
          companyID: value ?? 0,
          name: name ?? label,
          address: address ?? '',
          zip: zip ?? '',
          city: city ?? '',
          active: true,
        } as CompanyModel;

        opts.push({ label, value, extra: syntheticCompany });
      }
    }

    // Dedupe by label
    const seen = new Set<string>();
    return opts.filter((o) => {
      if (seen.has(String(o.label))) return false;
      seen.add(String(o.label));
      return true;
    });
  };

  const handleSave = async () => {
    const data = getValues();
    await onSave(data);
    modalStack.pop();
  };

  const handleCancel = () => {
    modalStack.pop();
  };

  return (
    <Modal
      title="Redigera originaladresser"
      buttons={[
        {
          icon: <FontAwesomeIcon icon={faSave} />,
          label: 'Spara',
          onClick: handleSave,
          disabled: !isDirty,
        },
        {
          icon: <FontAwesomeIcon icon={faCancel} />,
          label: 'Avbryt',
          onClick: handleCancel,
        },
      ]}
    >
      <WarningContainer>
        <FontAwesomeIcon icon={faWarning} />
        <div>
          <strong>Varning:</strong> Att ändra originaladresserna påverkar
          faktureringen och kan ha konsekvenser för ärendets hantering. Se till
          att ändringarna är korrekta innan du sparar.
        </div>
      </WarningContainer>

      <AddressSection>
        <SectionTitle>Original från-adress</SectionTitle>
        <AddressInput
          name={formData.originalFromName}
          address={formData.originalFromAddress}
          city={formData.originalFromCity}
          zip={formData.originalFromZip}
          countyId={formData.originalFromCounty}
          onNameChanged={(value) =>
            setValue('originalFromName', value, { shouldDirty: true })
          }
          onAddressChanged={(value) =>
            setValue('originalFromAddress', value, { shouldDirty: true })
          }
          onCityChanged={(value) =>
            setValue('originalFromCity', value, { shouldDirty: true })
          }
          onZipChanged={(value) =>
            setValue('originalFromZip', value, { shouldDirty: true })
          }
          onCountyChanged={(county) => {
            const countyId = county?.id.toString() ?? '';
            setValue('originalFromCounty', countyId, { shouldDirty: true });
          }}
          onCompanyPicked={(company, newCounty) =>
            handleCompanyPicked(company, newCounty, 'originalFrom')
          }
          companyOptionFilter={() => true}
          companyPickerTopOptions={buildTopOptions('from')}
        />
      </AddressSection>

      <AddressSection>
        <SectionTitle>Original till-adress</SectionTitle>
        <AddressInput
          name={formData.originalToName}
          address={formData.originalToAddress}
          city={formData.originalToCity}
          zip={formData.originalToZip}
          countyId={formData.originalToCounty}
          onNameChanged={(value) =>
            setValue('originalToName', value, { shouldDirty: true })
          }
          onAddressChanged={(value) =>
            setValue('originalToAddress', value, { shouldDirty: true })
          }
          onCityChanged={(value) =>
            setValue('originalToCity', value, { shouldDirty: true })
          }
          onZipChanged={(value) =>
            setValue('originalToZip', value, { shouldDirty: true })
          }
          onCountyChanged={(county) => {
            const countyId = county?.id.toString() ?? '';
            setValue('originalToCounty', countyId, { shouldDirty: true });
          }}
          onCompanyPicked={(company, newCounty) =>
            handleCompanyPicked(company, newCounty, 'originalTo')
          }
          companyOptionFilter={() => true}
          companyPickerTopOptions={buildTopOptions('to')}
        />
      </AddressSection>
    </Modal>
  );
};

export default OriginalAddressEditModal;
